import React from 'react';

import { LEGAL_DOCUMENT_META } from '@naumu/shared';
import type { LegalDocumentType } from '@naumu/shared';

// `title` + `lastUpdated` are owned by `@naumu/shared` (`LEGAL_DOCUMENT_META`)
// so the backend's legal-update broadcast gates on the same `lastUpdated` date
// the page renders. Only the rendered prose (`content`) lives here.
export type { LegalDocumentType };

export interface LegalDocument {
	title: string;
	lastUpdated: string;
	content: React.ReactNode;
}

export const LEGAL_DOCUMENTS: Record<LegalDocumentType, LegalDocument> = {
	'terms-of-service': {
		title: LEGAL_DOCUMENT_META['terms-of-service'].title,
		lastUpdated: LEGAL_DOCUMENT_META['terms-of-service'].lastUpdated,
		content: (
			<>
				<h2 className="font-semibold mt-6 first:mt-0">1. Acceptance of Terms</h2>
				<p>
					By accessing or using Naumu (&quot;the Service&quot;), operated by Harmonik
					Studio d.o.o., a company incorporated in the Republic of Croatia, you agree to be
					bound by these Terms of Service (&quot;Terms&quot;). If you do not agree,
					do not use the Service.
				</p>

				<h2 className="font-semibold mt-6">2. Description of Service</h2>
				<p>
					Naumu provides a platform for structuring ideas through graph visualization
					and AI-assisted exploration. Content generated by AI on the platform is for
					informational purposes and should not be relied upon as professional advice.
				</p>

				<h2 className="font-semibold mt-6">3. Account Registration</h2>
				<p>
					You must provide accurate information when creating an account. You are
					responsible for maintaining the security of your account credentials and
					for all activities under your account.
				</p>

				<h2 className="font-semibold mt-6">4. User Conduct</h2>
				<p>You agree not to:</p>
				<ul className="pl-5 list-disc space-y-1">
					<li>Use the Service for any unlawful purpose</li>
					<li>Upload malicious content or attempt to compromise the platform</li>
					<li>Interfere with other users&apos; access to the Service</li>
					<li>Reverse-engineer, decompile, or attempt to extract source code</li>
					<li>Use the Service to generate content that violates third-party rights</li>
				</ul>

				<h2 className="font-semibold mt-6">5. Intellectual Property</h2>
				<p>
					The platform design, features, and code are the property of Harmonik Studio d.o.o.
					You retain ownership of the ideas and data you input. By using the Service,
					you grant us a limited license to process your content solely to provide
					and improve the Service. AI-generated outputs are provided as-is; we do not
					claim ownership over them.
				</p>

				<h2 className="font-semibold mt-6">6. Third-Party Connections</h2>
				<p>
					You may connect third-party applications, such as AI clients, to your
					Naumu account using OAuth. A connected application acts on your behalf
					and with your permissions, and can read and modify the content in your
					spaces. You are responsible for the applications you authorize and for
					any content you choose to share with them. Content accessed through a
					connected application is also subject to that application
					provider&apos;s own terms and privacy policy, which we do not control.
					You can revoke a connected application&apos;s access at any time in
					Settings &gt; Connected Apps.
				</p>

				<h2 className="font-semibold mt-6">7. Data and Privacy</h2>
				<p>
					Your use of the Service is also governed by our{' '}
					<a href="/legal/privacy-policy" className="underline">Privacy Policy</a>.
					By using the Service, you consent to the collection and processing of data
					as described therein.
				</p>

				<h2 className="font-semibold mt-6">8. Subscriptions, Plans &amp; Billing</h2>
				<p>
					Naumu offers a free tier and paid plans (Team and Max). Free spaces do not
					require payment.
				</p>
				<p>
					Each paid space has one Owner who is the customer for that subscription.
					Only the Owner can purchase, change, or cancel the plan.
				</p>
				<p>
					Paid plans auto-renew monthly until cancelled. Prices are in EUR and
					exclude VAT and applicable taxes unless stated otherwise. Croatian VAT
					applies where required; EU B2B customers with valid VAT IDs are subject to
					reverse-charge. Payments are processed by Stripe; we do not store your
					full payment card details.
				</p>
				<p><strong>Mid-period changes:</strong></p>
				<ul className="pl-5 list-disc space-y-1">
					<li>Adding members: full monthly seat fee billed on your next invoice.</li>
					<li>Removing members or downgrading: no refunds or credits.</li>
					<li>
						Tier upgrades take effect immediately and cannot be reversed within
						the same billing period.
					</li>
					<li>
						Tier downgrades and cancellations take effect at the end of the
						current period.
					</li>
				</ul>
				<p>
					Reserve packs are non-refundable. Unused AI interactions do not roll over
					to the next period. If a renewal payment fails, your space may enter a
					past-due state with reduced functionality.
				</p>
				<p>
					<strong>Right of withdrawal (EU consumers).</strong> Because the Service
					is a digital service that begins immediately on payment, by purchasing a
					paid plan or reserve pack you expressly consent to immediate performance
					and waive your statutory 14-day withdrawal right (Directive 2011/83/EU
					Article 16(m); Croatian Consumer Protection Act). This does not apply to
					B2B customers.
				</p>

				<h2 className="font-semibold mt-6">9. Service Availability</h2>
				<p>
					We aim to provide continuous access but do not guarantee uninterrupted
					availability. We may modify, suspend, or discontinue features with
					reasonable notice. We will make reasonable efforts to notify you of
					significant changes.
				</p>

				<h2 className="font-semibold mt-6">10. Limitation of Liability</h2>
				<p>
					To the maximum extent permitted by applicable law, Harmonik Studio d.o.o. is not
					liable for any indirect, incidental, special, or consequential damages
					arising from your use of the Service. The Service is provided &quot;as
					is&quot; without warranties of any kind. Nothing in these Terms limits
					liability that cannot be excluded under applicable law.
				</p>

				<h2 className="font-semibold mt-6">11. Account Termination &amp; Ownership Transfer</h2>
				<p>
					You may delete your account at any time through account settings. If you
					own active paid spaces, you must first cancel those subscriptions or
					transfer ownership to another member; account deletion is blocked while
					paid spaces remain.
				</p>
				<p>
					<strong>Ownership transfer</strong> moves a subscription to the new owner,
					who becomes responsible for all subsequent charges. The previous owner
					remains responsible for charges incurred before the transfer.
				</p>
				<p>
					<strong>Deleting a paid space</strong> cancels its subscription immediately
					with no refund for the remainder of the billing period.
				</p>
				<p>
					We may suspend or terminate accounts that violate these Terms. Upon
					termination, your data will be deleted in accordance with our Privacy
					Policy, except for billing and tax records we are legally required to
					retain.
				</p>

				<h2 className="font-semibold mt-6">12. Changes to Terms</h2>
				<p>
					We may update these Terms from time to time. We will notify you of material
					changes via email or an in-app notice. Continued use after changes take
					effect constitutes acceptance.
				</p>

				<h2 className="font-semibold mt-6">13. Governing Law & Disputes</h2>
				<p>
					These Terms are governed by the laws of the Republic of Croatia. Any disputes
					shall be resolved by the competent courts in Zagreb, Croatia. If you are a
					consumer in the EU, you retain any mandatory consumer protection rights
					under the law of your country of residence.
				</p>

				<h2 className="font-semibold mt-6">14. Severability</h2>
				<p>
					If any provision of these Terms is held to be invalid or unenforceable,
					the remaining provisions will remain in full force and effect.
				</p>

				<h2 className="font-semibold mt-6">15. Entire Agreement</h2>
				<p>
					These Terms, together with our Privacy Policy and Cookie Policy,
					constitute the entire agreement between you and us regarding the Service
					and supersede any prior agreements on the same subject matter.
				</p>

				<h2 className="font-semibold mt-6">16. Contact</h2>
				<p>
					For questions about these Terms, contact us at{' '}
					<a href="mailto:admin@naumu.ai?subject=Terms%20of%20Service%20enquiry" className="underline">admin@naumu.ai</a>.
				</p>
			</>
		)
	},
	'privacy-policy': {
		title: LEGAL_DOCUMENT_META['privacy-policy'].title,
		lastUpdated: LEGAL_DOCUMENT_META['privacy-policy'].lastUpdated,
		content: (
			<>
				<p className="mt-0">
					Harmonik Studio d.o.o. (&quot;we&quot;, &quot;us&quot;) is the data controller for
					the personal data processed through Naumu. We are committed to protecting
					your privacy in compliance with the EU General Data Protection Regulation
					(GDPR) and applicable data protection laws.
				</p>

				<h2 className="font-semibold mt-6">1. Information We Collect</h2>
				<p>We collect the following categories of data:</p>
				<ul className="pl-5 list-disc space-y-1">
					<li>
						<strong>Account data:</strong> Name, email address, and profile picture
						(provided via Google OAuth)
					</li>
					<li>
						<strong>Content data:</strong> Graph structures, nodes, edges, chat
						messages, notes, and other content you create
					</li>
					<li>
						<strong>Usage data:</strong> Feature interactions, page views, and
						performance metrics (only with your analytics consent)
					</li>
					<li>
						<strong>Technical data:</strong> Browser type, device information, and
						IP address (for security and abuse prevention)
					</li>
					<li>
						<strong>Billing data:</strong> If you upgrade to a paid plan, the
						identifiers required to operate the subscription (Stripe customer and
						subscription IDs, billing email, country, VAT details where required,
						last four digits and brand of your payment card for display purposes,
						and invoice history). We do not store full payment card numbers — these
						are handled directly by Stripe.
					</li>
					<li>
						<strong>Connected-app data:</strong> If you connect a third-party
						application (such as an AI client) to your Naumu account, we store the
						identity of the connected application, the date you authorized it, and
						the access tokens needed to operate the connection. You can view and
						revoke connected applications at any time in Settings &gt; Connected Apps.
					</li>
				</ul>

				<h2 className="font-semibold mt-6">2. How We Use Your Data</h2>
				<ul className="pl-5 list-disc space-y-1">
					<li>To provide, maintain, and improve the Service (legal basis: contract performance)</li>
					<li>To process your content through AI models for graph and chat features (legal basis: contract performance)</li>
					<li>To send service-related communications such as security alerts (legal basis: legitimate interest)</li>
					<li>To send product updates and tips, if you opted in (legal basis: consent)</li>
					<li>To analyze usage patterns and improve the product (legal basis: consent, via analytics cookies)</li>
					<li>To process payments, manage subscriptions, issue invoices, and meet tax and accounting obligations (legal basis: contract performance and legal obligation)</li>
				</ul>

				<h2 className="font-semibold mt-6">3. Sub-processors & Data Sharing</h2>
				<p>
					We do not sell your personal data. We share data with the following
					service providers who assist in operating the Service:
				</p>
				<ul className="pl-5 list-disc space-y-2">
					<li>
						<strong>Google Cloud (Vertex AI)</strong> (US) — OAuth authentication
						and our primary AI provider: it processes your content for graph and
						chat inference, generates the embeddings that power search, and
						transcribes audio you record. Per Google Cloud&apos;s terms, your data
						is not used to train Google&apos;s models.
					</li>
					<li>
						<strong>Anthropic</strong> (US) — additional AI model provider for graph
						and chat features. Your content is sent for processing and is not used
						for model training.
					</li>
					<li>
						<strong>PostHog</strong> (EU, Frankfurt) — feature flags and product
						analytics. Analytics capture runs only with your consent (see our Cookie
						Policy); feature flags are functional and always on.
					</li>
					<li>
						<strong>Dokploy</strong> (EU) — Infrastructure hosting provider.
					</li>
					<li>
						<strong>Stripe Payments Europe, Ltd.</strong> (Ireland, with
						infrastructure in the US) — payment processing, subscription billing,
						invoicing, and customer portal. Card data is collected and processed
						directly by Stripe; we never see or store full card numbers.
					</li>
				</ul>

				<h3 className="font-medium mt-4 text-sm">Third-party applications you connect</h3>
				<p>
					Naumu lets you connect third-party applications — for example AI clients
					such as Claude (Anthropic) or ChatGPT (OpenAI) — to your account using
					OAuth. When you authorize such an application, it can read and act on the
					content in your spaces on your behalf, including your graphs, nodes,
					threads, and notes, using your own access and permissions. To
					provide this functionality, the content you or the connected application
					access is transmitted to that application&apos;s provider, where it is
					handled under that provider&apos;s own privacy policy and terms, which we
					do not control. We share data with these applications only on your
					instruction, and only for as long as the connection remains authorized.
					You can revoke a connected application&apos;s access at any time from
					Settings &gt; Connected Apps; revocation takes effect immediately.
				</p>

				<h2 className="font-semibold mt-6">4. International Data Transfers</h2>
				<p>
					Some of our sub-processors (Anthropic, Google, and Stripe) operate
					infrastructure in the United States. Transfers to these processors are
					covered by Standard Contractual Clauses (SCCs) approved by the European
					Commission, or by adequacy decisions where applicable.
				</p>

				<h2 className="font-semibold mt-6">5. Data Retention</h2>
				<ul className="pl-5 list-disc space-y-1">
					<li>
						<strong>Account data:</strong> Retained while your account is active
						and deleted within 30 days of account deletion
					</li>
					<li>
						<strong>Content data:</strong> Retained while your account is active;
						deleted when you delete content or your account
					</li>
					<li>
						<strong>Usage/analytics data:</strong> Retained for up to 12 months,
						then anonymized or deleted
					</li>
					<li>
						<strong>Session data:</strong> Expires after 365 days of inactivity
					</li>
					<li>
						<strong>Connected-app data:</strong> Retained while the connection is
						authorized; deleted immediately when you revoke the app (Settings &gt;
						Connected Apps) or delete your account
					</li>
					<li>
						<strong>Billing records (invoices, transactions, tax data):</strong>{' '}
						Retained for 11 years to comply with Croatian tax and accounting law
						(General Tax Act and Accounting Act), even after account deletion.
						Personal data within these records is retained only to the extent
						legally required.
					</li>
				</ul>

				<h2 className="font-semibold mt-6">6. Your Rights (GDPR)</h2>
				<p>Under the GDPR, you have the right to:</p>
				<ul className="pl-5 list-disc space-y-1">
					<li><strong>Access</strong> — Request a copy of your personal data</li>
					<li><strong>Rectification</strong> — Correct inaccurate data (you can edit your name and avatar in Settings)</li>
					<li><strong>Erasure</strong> — Delete your account and data (available in Settings &gt; Delete account)</li>
					<li><strong>Data portability</strong> — Export your spaces (available via the export feature in-app)</li>
					<li><strong>Withdraw consent</strong> — Revoke marketing consent at any time via Settings</li>
					<li><strong>Object</strong> — Object to processing based on legitimate interest</li>
					<li><strong>Lodge a complaint</strong> — File a complaint with your local data protection authority</li>
					<li><strong>Manage connected apps</strong> — View and revoke any third-party applications you have connected to your account (available in Settings &gt; Connected Apps). Revoking an application immediately ends its access to your spaces.</li>
				</ul>
				<p>
					To exercise rights not available through the app, contact{' '}
					<a href="mailto:admin@naumu.ai?subject=Data%20rights%20request" className="underline">admin@naumu.ai</a>.
					We will respond within 30 days. Note that erasure cannot remove billing
					records we are legally required to retain; those are kept for the
					statutory period and then deleted.
				</p>

				<h2 className="font-semibold mt-6">7. Data Security</h2>
				<p>
					We implement industry-standard security measures including encrypted
					connections (TLS), secure session management, and access controls. While
					no system is perfectly secure, we take reasonable measures to protect your
					data.
				</p>

				<h2 className="font-semibold mt-6">8. Changes to This Policy</h2>
				<p>
					We may update this policy from time to time. Material changes will be
					communicated via email or in-app notice. The &quot;Last updated&quot; date
					at the top reflects the latest revision.
				</p>

				<h2 className="font-semibold mt-6">9. Contact</h2>
				<p>
					For privacy-related questions or to exercise your rights, contact us at{' '}
					<a href="mailto:admin@naumu.ai?subject=Privacy%20enquiry" className="underline">admin@naumu.ai</a>.
				</p>
			</>
		)
	},
	'cookie-policy': {
		title: LEGAL_DOCUMENT_META['cookie-policy'].title,
		lastUpdated: LEGAL_DOCUMENT_META['cookie-policy'].lastUpdated,
		content: (
			<>
				<p className="mt-0">
					This Cookie Policy explains how Naumu uses cookies and similar
					technologies to operate the Service.
				</p>

				<h2 className="font-semibold mt-6">1. What Are Cookies?</h2>
				<p>
					Cookies are small text files stored on your device. We also use
					localStorage, a browser-based storage mechanism that works similarly.
				</p>

				<h2 className="font-semibold mt-6">2. Cookies We Use</h2>
				<p>
					Most cookies and local storage used by Naumu are essential for the
					Service to function and cannot be disabled without breaking core
					functionality. Product-analytics capture is the exception: it runs only
					if you accept it in the consent banner, and you can decline it without
					affecting how Naumu works.
				</p>

				<h3 className="font-medium mt-4 text-sm">Authentication</h3>
				<ul className="pl-5 list-disc space-y-1">
					<li>
						<strong>connect.sid</strong> — Session cookie that keeps you signed in.
						Expires after 365 days of inactivity.
					</li>
				</ul>

				<h3 className="font-medium mt-4 text-sm">Service delivery (PostHog)</h3>
				<p>
					PostHog powers feature flags that control what functionality is
					available to you — these are functional and always on. It also provides
					product analytics, but analytics capture runs only with your consent:
					nothing is captured until you accept it in the consent banner, and
					declining leaves feature flags working. Data is processed in the EU
					(Frankfurt). Uses a combination of cookies and localStorage.
				</p>

				<h3 className="font-medium mt-4 text-sm">Preferences</h3>
				<ul className="pl-5 list-disc space-y-1">
					<li>
						<strong>Theme</strong> — Remembers your light/dark mode choice
						(localStorage).
					</li>
					<li>
						<strong>Notification settings</strong> — Stores your notification
						and sound preferences (localStorage).
					</li>
				</ul>

				<h2 className="font-semibold mt-6">3. Third-Party Cookies</h2>
				<p>
					We do not use advertising cookies. Google may set cookies during
					the OAuth sign-in flow, governed by{' '}
					<a
						href="https://policies.google.com/privacy"
						target="_blank"
						rel="noopener noreferrer"
						className="underline"
					>
						Google&apos;s Privacy Policy
					</a>. Stripe sets cookies on its hosted checkout and customer billing
					portal pages when you upgrade or manage a subscription, governed by{' '}
					<a
						href="https://stripe.com/privacy"
						target="_blank"
						rel="noopener noreferrer"
						className="underline"
					>
						Stripe&apos;s Privacy Policy
					</a>.
				</p>

				<h2 className="font-semibold mt-6">4. Managing Cookies</h2>
				<p>
					You can clear or block cookies through your browser settings. Note that
					doing so will sign you out and may prevent the Service from functioning
					correctly.
				</p>

				<h2 className="font-semibold mt-6">5. Contact</h2>
				<p>
					For questions about our use of cookies, contact{' '}
					<a href="mailto:admin@naumu.ai?subject=Cookie%20policy%20enquiry" className="underline">admin@naumu.ai</a>.
				</p>
			</>
		)
	}
};
